import dayjs from "dayjs";
import {
  type IChart,
  type TChartValueSource,
} from "src/redux/slices/entriesGeneratorSlice";

type Element = string | number;

export const isNumericValue = (val: unknown) =>
  val !== undefined && val !== null && val !== "" && isFinite(Number(val));

const GROUPED_FROM = 10000;
const NO_BREAK_SPACE = "\u00A0";
const decimals = { maximumFractionDigits: 2 };
const plainNumber = new Intl.NumberFormat("en-US", {
  ...decimals,
  useGrouping: false,
});
const groupedNumber = new Intl.NumberFormat("en-US", decimals);
const WORDED_FROM = 10000000;
// Spelled out: on a ship a lone T or M reads as tonnes or metres.
const wordedNumber = new Intl.NumberFormat("en-US", {
  ...decimals,
  notation: "compact",
  compactDisplay: "long",
});

// Dot decimals like the logbook's numeric fields, whatever the browser's
// language; big values get spaced thousands, the biggest are worded.
export const formatNumber = (value: number) => {
  const size = Math.abs(value);
  if (size < GROUPED_FROM) return plainNumber.format(value);
  if (size >= WORDED_FROM) return wordedNumber.format(value);
  return groupedNumber.format(value).replaceAll(",", NO_BREAK_SPACE);
};

export const isChartNumeric = (chart: Pick<IChart, "dataType" | "rawVals">) =>
  chart.dataType
    ? chart.dataType === "numeric"
    : chart.rawVals.some(isNumericValue);

export const resolveValueSource = (
  chart: Pick<IChart, "type" | "valueSource">,
): TChartValueSource => {
  const source =
    chart.valueSource ??
    ({ kind: chart.type === "pie" ? "count" : "entries" } as TChartValueSource);
  return chart.type === "pie" && source.kind === "entries"
    ? { kind: "count" }
    : source;
};

export interface IChartModel {
  labels: string[];
  values: number[];
  rowsByLabel: number[][];
  otherLabels?: string[];
}

export const OTHER_LABEL = "Other";
export const MAX_PIE_SLICES = 12;

type TChartModelInput = Pick<
  IChart,
  | "type"
  | "valueSource"
  | "rawVals"
  | "rowRefs"
  | "sumVals"
  | "sortBy"
  | "sortDirection"
  | "groupSmall"
  | "filter"
  | "filterVals"
> &
  Partial<Pick<IChart, "name">>;

const isBlank = (val: unknown) =>
  val === undefined || val === null || val === "";

const sortModel = (
  model: IChartModel,
  sortBy?: "label" | "value",
  direction: "asc" | "desc" = "asc",
): IChartModel => {
  if (!sortBy) return model;
  const { labels, values, rowsByLabel } = model;

  const indices = labels.map((_, i) => i);
  indices.sort((a, b) => {
    const cmp =
      sortBy === "value"
        ? values[a] - values[b]
        : labels[a].localeCompare(labels[b], undefined, { numeric: true });
    return direction === "desc" ? -cmp : cmp;
  });

  return {
    labels: indices.map(i => labels[i]),
    values: indices.map(i => values[i]),
    rowsByLabel: indices.map(i => rowsByLabel[i]),
  };
};

const groupSmallSlices = (model: IChartModel): IChartModel => {
  if (model.labels.length <= MAX_PIE_SLICES) return model;

  const kept = new Set(
    model.values
      .map((value, i) => ({ value, i }))
      .sort((a, b) => b.value - a.value)
      .slice(0, MAX_PIE_SLICES - 1)
      .map(({ i }) => i),
  );
  const result: Required<IChartModel> = {
    labels: [],
    values: [],
    rowsByLabel: [],
    otherLabels: [],
  };
  let otherValue = 0;
  const otherRows: number[] = [];

  model.labels.forEach((label, i) => {
    if (kept.has(i)) {
      result.labels.push(label);
      result.values.push(model.values[i]);
      result.rowsByLabel.push(model.rowsByLabel[i]);
      return;
    }
    result.otherLabels.push(label);
    otherValue += model.values[i];
    otherRows.push(...model.rowsByLabel[i]);
  });

  result.labels.push(OTHER_LABEL);
  result.values.push(Math.round(otherValue * 100) / 100);
  result.rowsByLabel.push(otherRows);
  return result;
};

export const hasFilter = (chart: Pick<IChart, "filter">) =>
  !!chart.filter?.values.length;

const getRowFilter = (chart: TChartModelInput) => {
  if (!chart.filter || !hasFilter(chart)) return () => true;
  const source =
    chart.filter.column === chart.name ? chart.rawVals : chart.filterVals;
  if (!source) return () => true;
  const allowed = new Set(chart.filter.values);
  return (index: number) => allowed.has(String(source[index]));
};

export const formatFilter = (chart: Pick<IChart, "name" | "filter">) => {
  if (!chart.filter || !hasFilter(chart)) return "";
  const { column, values } = chart.filter;
  const shown = values.slice(0, 4).join(", ");
  const more = values.length > 4 ? ` +${values.length - 4}` : "";
  return column === chart.name
    ? ` (only ${shown}${more})`
    : ` (${column}: ${shown}${more})`;
};

export const getDefaultChartTitle = (
  chart: Pick<IChart, "name" | "type" | "valueSource" | "filter">,
) => {
  const valueSource = resolveValueSource(chart);
  const title =
    valueSource.kind === "sum"
      ? `Sum of ${valueSource.column} by ${chart.name}`
      : valueSource.kind === "count"
        ? `Count of entries by ${chart.name}`
        : chart.name;
  return title + formatFilter(chart);
};

export const buildChartModel = (chart: TChartModelInput): IChartModel => {
  const valueSource = resolveValueSource(chart);
  const isRowAllowed = getRowFilter(chart);

  if (valueSource.kind === "entries") {
    const model: IChartModel = { labels: [], values: [], rowsByLabel: [] };
    chart.rawVals.forEach((val, i) => {
      if (isBlank(val) || !isRowAllowed(i)) return;
      model.labels.push(dayjs(chart.rowRefs[i]?.date).format("D MMM YYYY"));
      model.values.push(Number(val));
      model.rowsByLabel.push([i]);
    });
    return model;
  }

  // Map, not an object: integer-like keys would be reordered.
  const groups = new Map<string, { value: number; rows: number[] }>();
  chart.rawVals.forEach((label, i) => {
    if (isBlank(label) || !isRowAllowed(i)) return;
    const sumVal = chart.sumVals?.[i];
    if (valueSource.kind === "sum" && !isNumericValue(sumVal)) return;

    const key = String(label);
    const group = groups.get(key) ?? { value: 0, rows: [] };
    group.value += valueSource.kind === "sum" ? Number(sumVal) : 1;
    group.rows.push(i);
    groups.set(key, group);
  });

  const grouped = [...groups.values()];
  const model = sortModel(
    {
      labels: [...groups.keys()],
      values: grouped.map(({ value }) => Math.round(value * 100) / 100),
      rowsByLabel: grouped.map(({ rows }) => rows),
    },
    chart.sortBy,
    chart.sortDirection,
  );
  return chart.type === "pie" && chart.groupSmall !== false
    ? groupSmallSlices(model)
    : model;
};

const isFilled = (val: unknown): val is string | number =>
  val !== undefined && val !== null && String(val).trim() !== "";

// get-info-list table -> code: description. The code is the cell at `row`.
export const flattenInfoList = (ildata: any): Record<string, string> => {
  const codeIndex = Number(ildata?.row ?? 0);
  const descriptions: Record<string, string> = {};

  (ildata?.sections ?? []).forEach((section: any) => {
    (section?.rows ?? []).forEach((row: any) => {
      if (!Array.isArray(row)) return;
      const code = row[codeIndex]?.name;
      if (!isFilled(code)) return;

      const description = row
        .filter((_, i) => i !== codeIndex)
        .map(cell => cell?.name)
        .filter(isFilled)
        .map(text => String(text).trim())
        .join(" - ");
      const key = String(code).trim();
      if (description && !(key in descriptions))
        descriptions[key] = description;
    });
  });

  return descriptions;
};

const FIT_TOLERANCE = 0.85;

interface IPieLabel {
  index: number;
  value: number;
  midAngle: number;
  span: number;
  width: number;
  height: number;
}

// A label must fit across its slice; on overlap the bigger slice wins.
export const pickPieLabels = (
  slices: IPieLabel[],
  radius: number,
  offset: number,
) => {
  const kept: Array<{ x: number; y: number; width: number; height: number }> =
    [];
  const shown = new Set<number>();

  [...slices]
    .sort((a, b) => b.value - a.value)
    .forEach(({ index, midAngle, span, width, height }) => {
      const cos = Math.cos(midAngle);
      const sin = Math.sin(midAngle);
      const distance =
        radius -
        offset -
        (Math.abs(cos) * width) / 2 -
        (Math.abs(sin) * height) / 2;
      const across = Math.abs(sin) * width + Math.abs(cos) * height;
      if (span < Math.PI && span * distance < across * FIT_TOLERANCE) return;
      const box = { x: cos * distance, y: sin * distance, width, height };
      const overlaps = kept.some(
        other =>
          Math.abs(box.x - other.x) < (box.width + other.width) / 2 &&
          Math.abs(box.y - other.y) < (box.height + other.height) / 2,
      );
      if (overlaps) return;
      kept.push(box);
      shown.add(index);
    });

  return shown;
};

const round2 = (value: number) => Math.round(value * 100) / 100;

export const sumNumeric = (values: unknown[]) => {
  const numbers = values.filter(isNumericValue).map(Number);
  return numbers.length
    ? round2(numbers.reduce((sum, value) => sum + value, 0))
    : null;
};

export const truncateText = (text: string, max: number) =>
  text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;

export const wrapText = (text: string, max = 48) => {
  const lines: string[] = [];
  text.split(/\s+/).forEach(word => {
    const last = lines[lines.length - 1];
    if (last !== undefined && `${last} ${word}`.length <= max)
      lines[lines.length - 1] = `${last} ${word}`;
    else lines.push(word);
  });
  return lines;
};

export const CHART_GAP = 32;
const MIN_CHART_WIDTH = 520;
const MAX_CHART_WIDTH = 900;
const toEven = (value: number) => Math.floor(value / 2) * 2;

// Whole, even pixels: a canvas on a fractional pixel renders blurry.
export const getChartLayout = (containerWidth: number, chartCount: number) => {
  if (containerWidth <= 0) return { columns: 1, width: MIN_CHART_WIDTH };
  const columns = Math.max(
    1,
    Math.min(
      chartCount,
      Math.floor((containerWidth + CHART_GAP) / (MIN_CHART_WIDTH + CHART_GAP)),
    ),
  );
  const width = (containerWidth - CHART_GAP * (columns - 1)) / columns;
  return { columns, width: toEven(Math.min(width, MAX_CHART_WIDTH)) };
};

export const getChartHeight = (width: number) => toEven(width * 0.6);

const PALETTE = [
  "255, 99, 132",
  "54, 162, 235",
  "255, 206, 86",
  "75, 192, 192",
  "153, 102, 255",
  "255, 159, 64",
  "46, 160, 67",
  "214, 51, 132",
  "32, 201, 151",
  "111, 66, 193",
  "139, 90, 43",
  "13, 110, 253",
] as const;
const OTHER_COLOR = ["rgba(134, 142, 150, 1)", "rgba(134, 142, 150, 0.5)"];

const getColor = (index: number) => {
  if (index < PALETTE.length)
    return [`rgba(${PALETTE[index]}, 1)`, `rgba(${PALETTE[index]}, 0.5)`];
  const hue = Math.round((index * 137.508) % 360);
  return [`hsla(${hue}, 65%, 50%, 1)`, `hsla(${hue}, 65%, 50%, 0.5)`];
};

export const getColorsForLabels = (labels: Element[]) => {
  const sortedUnique = [...new Set(labels.map(String))]
    .filter(label => label !== OTHER_LABEL)
    .sort();
  const colorByLabel = new Map(
    sortedUnique.map((label, i) => [label, getColor(i)]),
  );
  const colors = labels.map(l => colorByLabel.get(String(l)) ?? OTHER_COLOR);
  return {
    borderColor: colors.map(color => color[0]),
    backgroundColor: colors.map(color => color[1]),
  };
};

/**
 * Calculates a trendline for a given set of numerical values.
 *
 * @param vals - An array of numerical values for which the trendline is calculated.
 * @returns An array of trendline values corresponding to the input values.
 */
export const trendline = (vals: number[]) => {
  const n = vals.length;
  if (n < 2) return [...vals];
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumX2 = 0;

  for (let i = 0; i < n; i++) {
    sumX += i;
    sumY += vals[i];
    sumXY += i * vals[i];
    sumX2 += i ** 2;
  }

  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX ** 2);
  const intercept = (sumY - slope * sumX) / n;

  return Array.from({ length: n }, (_, i) => slope * i + intercept);
};
